import {
  ArrowRightOutlined,
  CheckCircleFilled,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Alert } from 'antd';
import {
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { withNamespaces } from 'react-i18next';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import { POSTHOG_EVENTS, useAnalytics } from '../../../analytics/posthog';
import { fetchComplianceWatchAggregatedData } from '../../../api/compliance-watch-snapshots';
import Box from '../../../components/Box';
import LoadingSkeleton from '../../../components/LoadingSkeleton';
import Text from '../../../components/Text';
import FadedText from '../../../components/Text/FadedText';
import LinkText from '../../../components/Text/LinkText';
import { INTERNAL_LINKS } from '../../../enum';
import { RATE_TYPES } from '../../../enum/Rates';
import { getMinutesAsMilliseconds } from '../../../utils/numbers';
import classNames from './styles.module.scss';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip);

const pointValueLabelsPlugin = {
  id: 'complianceWatchPointValueLabels',
  afterDatasetsDraw: chart => {
    const { ctx } = chart;

    ctx.save();
    ctx.fillStyle = '#767b84';
    ctx.font = '9px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    chart.getDatasetMeta(0).data.forEach((point, index) => {
      ctx.fillText(chart.data.datasets[0].data[index], point.x, point.y - 16);
    });

    ctx.restore();
  },
};

const COMPONENT_DETAILS = {
  insuranceStatus: {
    labelKey: 'insurance',
    detailKey: 'complianceWatchInsuranceMissing',
    tone: 'insurance',
  },
  businessVehicleStatus: {
    labelKey: 'vehicle',
    detailKey: 'complianceWatchBusinessVehicleMissing',
    tone: 'vehicle',
  },
  businessMileageStatus: {
    labelKey: 'businessMileage',
    detailKey: 'complianceWatchBusinessMileageMissing',
    tone: 'mileage',
  },
};

const isComplianceWatchEligible = (currentCompany, groupList) => {
  if (currentCompany?.hasFavrProducts) return true;

  return groupList.some(group => {
    const product = group?.productId;
    const isFavr = product?.rateType === RATE_TYPES.FAVR;
    const isComplianceEnabledCpm =
      product?.rateType === RATE_TYPES.FLAT && !!product?.insuranceComplianceType;

    return isFavr || isComplianceEnabledCpm;
  });
};

const formatDriverName = user => [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '—';

const getAtRiskDetails = (t, user) => {
  if (user?.atRiskSituations?.includes('expiringInsurance')) {
    return {
      description: user.insuranceExpirationDate
        ? t('complianceWatchInsuranceExpiresOn', {
            date: moment(user.insuranceExpirationDate).format('MMM D'),
          })
        : t('complianceWatchInsuranceExpiring'),
      tags: [{ label: t('insurance'), tone: 'insurance' }],
    };
  }

  return {
    description: t('complianceWatchVehicleAgingOut', {
      year: user?.vehicleYear,
    }),
    tags: [{ label: t('vehicle'), tone: 'vehicle' }],
  };
};

const DriverRow = ({ showStatusBadge, t, user }) => {
  const isOutOfCompliance = user.type === 'out-of-compliance';
  const components = (user.oocComponents || [])
    .map(component => COMPONENT_DETAILS[component])
    .filter(Boolean);
  const atRiskDetails = getAtRiskDetails(t, user);
  const description = isOutOfCompliance
    ? components.map(component => t(component.detailKey)).join(' · ')
    : atRiskDetails.description;
  const tags = isOutOfCompliance
    ? components.map(component => ({ label: t(component.labelKey), tone: component.tone }))
    : atRiskDetails.tags;

  return (
    <li className={classNames.driverRow}>
      <span
        className={[
          classNames.statusDot,
          isOutOfCompliance ? classNames.dangerDot : classNames.riskDot,
        ].join(' ')}
      />
      <div className={classNames.driverDetails}>
        <div className={classNames.driverHeading}>
          <Text variant="strong" size="xs" renderAs="span" className={classNames.driverName}>
            {formatDriverName(user)}
          </Text>
          {showStatusBadge && (
            <Text
              variant="strong"
              size="xs"
              renderAs="span"
              className={[
                classNames.statusBadge,
                isOutOfCompliance ? classNames.dangerBadge : classNames.riskBadge,
              ].join(' ')}
            >
              {t(isOutOfCompliance ? 'complianceWatchNew' : 'complianceWatchAtRisk')}
            </Text>
          )}
        </div>
        <FadedText size="xs" renderAs="span" className={classNames.driverDescription}>
          {description}
        </FadedText>
      </div>
      <div className={classNames.tags}>
        {tags.map(tag => (
          <Text
            key={`${tag.label}-${tag.tone}`}
            variant="strong"
            size="xs"
            renderAs="span"
            className={[classNames.issueTag, classNames[`${tag.tone}Tag`]].join(' ')}
          >
            {tag.label}
          </Text>
        ))}
      </div>
    </li>
  );
};

const TrendChart = ({ points, trend }) => {
  const chronologicalPoints = [...points].reverse();
  const values = chronologicalPoints.map(point => point.count);
  const trendColor = {
    down: '#16a34a',
    flat: '#94a3b8',
    up: '#ff6161',
  }[trend];

  return (
    <div className={classNames.trendChart}>
      <Line
        role="img"
        aria-label="Out-of-compliance driver trend"
        data={{
          labels: chronologicalPoints.map(point =>
            moment({ year: point.year, month: point.month - 1 }).format('MMM'),
          ),
          datasets: [
            {
              data: values,
              borderColor: trendColor,
              borderWidth: 2,
              pointBackgroundColor: '#ffffff',
              pointBorderColor: trendColor,
              pointBorderWidth: 2,
              pointHoverRadius: 5,
              pointRadius: context =>
                context.dataIndex === chronologicalPoints.length - 1 ? 5 : 3.5,
              tension: 0,
            },
          ],
        }}
        options={{
          animation: false,
          maintainAspectRatio: false,
          layout: {
            padding: {
              left: 6,
              right: 6,
              top: 24,
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
          scales: {
            x: {
              grid: {
                color: '#eeeff1',
                drawOnChartArea: false,
                drawTicks: false,
              },
              ticks: {
                color: '#a8adb8',
                font: { size: 9 },
                padding: 8,
              },
            },
            y: {
              beginAtZero: true,
              display: false,
              grace: '25%',
            },
          },
        }}
        plugins={[pointValueLabelsPlugin]}
      />
    </div>
  );
};

const LoadingState = () => (
  <Box className={classNames.widget}>
    <LoadingSkeleton className={classNames.headerSkeleton} />
    <div className={classNames.loadingGrid}>
      <LoadingSkeleton />
      <LoadingSkeleton />
    </div>
  </Box>
);

const ComplianceWatch = ({ currentCompany, groupList, t }) => {
  const { capture } = useAnalytics();
  const isEligible = useMemo(() => isComplianceWatchEligible(currentCompany, groupList), [
    currentCompany,
    groupList,
  ]);

  const complianceWatchQuery = useQuery({
    enabled: isEligible && !!currentCompany?._id,
    queryKey: ['complianceWatchAggregatedData', currentCompany?._id],
    queryFn: () => fetchComplianceWatchAggregatedData(currentCompany._id),
    staleTime: getMinutesAsMilliseconds(5),
    retry: 1,
  });

  if (!isEligible) return null;
  if (complianceWatchQuery.isLoading) return <LoadingState />;

  if (complianceWatchQuery.isError) {
    return (
      <Box className={classNames.widget}>
        <div className={classNames.widgetHeader}>
          <Text variant="strong" size="sm" renderAs="h2" className={classNames.title}>
            {t('complianceWatch')}
          </Text>
        </div>
        <Alert
          showIcon
          type="warning"
          message={t('complianceWatchUnavailable')}
          description={t('complianceWatchUnavailableDescription')}
        />
      </Box>
    );
  }

  const data = complianceWatchQuery.data || {};
  const currentOocCount = data.currentMonthOocUsersCount || 0;
  const previousOocCount = data.oocUsers?.[1]?.count || 0;
  const oocCountChange = currentOocCount - previousOocCount;
  const newOocCount = Math.max(oocCountChange, 0);
  const isProactive = currentOocCount === 0;
  const hasActiveChanges = oocCountChange > 0;
  const trend = oocCountChange > 0 ? 'up' : oocCountChange < 0 ? 'down' : 'flat';
  const users = data.users || [];
  const outOfComplianceUsers = users.filter(user => user.type === 'out-of-compliance');
  const atRiskUsers = users.filter(user => user.type === 'at-risk');
  const hiddenUsersCount = Math.max(currentOocCount + (data.atRiskCount || 0) - users.length, 0);
  const previousMonthLabel = data.oocUsers?.[1]
    ? moment({
        year: data.oocUsers[1].year,
        month: data.oocUsers[1].month - 1,
      }).format('MMM')
    : t('complianceWatchLastMonth');

  const trackComplianceWatchInteraction = interactionType => {
    capture(POSTHOG_EVENTS.COMPLIANCE_WATCH_INTERACTION, {
      interaction_type: interactionType,
      surface: 'dashboard_compliance_watch',
      is_proactive: isProactive,
      trend,
      has_active_changes: hasActiveChanges,
      current_ooc_count: currentOocCount,
      at_risk_count: data.atRiskCount || 0,
      grace_period_count: data.gracePeriodCount || 0,
      hidden_users_count: hiddenUsersCount,
    });
  };

  const renderDriverRows = (drivers, showStatusBadge) =>
    drivers.map((user, index) => {
      const shouldShowStatusBadge =
        typeof showStatusBadge === 'function' ? showStatusBadge(user) : showStatusBadge;

      return (
        <DriverRow
          key={`${user.type}-${user.firstName}-${user.lastName}-${index}`}
          showStatusBadge={shouldShowStatusBadge}
          t={t}
          user={user}
        />
      );
    });

  return (
    <Box className={classNames.widget}>
      <div className={classNames.widgetHeader}>
        <div className={classNames.titleGroup}>
          <span className={classNames.titleIcon}>
            <SafetyCertificateOutlined />
          </span>
          <Text variant="strong" size="sm" renderAs="h2" className={classNames.title}>
            {t('complianceWatch')}
          </Text>
        </div>
        <Link
          to={INTERNAL_LINKS.USER_PROGRAM_COMPLIANCE}
          className={classNames.reportLink}
          onClick={() => trackComplianceWatchInteraction('header_report_link_click')}
        >
          <LinkText variant="strong" size="xs" className={classNames.linkText}>
            {t('viewFullReport')} <ArrowRightOutlined />
          </LinkText>
        </Link>
      </div>

      <div className={classNames.widgetContent}>
        <section className={classNames.summaryPanel}>
          <div className={classNames.primaryMetric}>
            <div>
              <strong
                className={[
                  classNames.metricValue,
                  isProactive ? classNames.successMetric : classNames.dangerMetric,
                ].join(' ')}
              >
                {currentOocCount}
              </strong>
              <FadedText size="xs" renderAs="span" className={classNames.metricLabel}>
                {t('complianceWatchDriversOutOfCompliance', { count: currentOocCount })}
              </FadedText>
            </div>
          </div>

          <Text
            size="xs"
            renderAs="p"
            className={[
              classNames.monthlyChange,
              trend === 'up'
                ? classNames.negativeChange
                : trend === 'down'
                ? classNames.positiveChange
                : classNames.neutralChange,
            ].join(' ')}
          >
            {trend === 'up' &&
              t('complianceWatchIncreaseFromMonth', {
                count: oocCountChange,
                month: previousMonthLabel,
              })}
            {trend === 'flat' &&
              t('complianceWatchSameAsMonth', {
                month: previousMonthLabel,
              })}
            {trend === 'down' &&
              t('complianceWatchDecreaseFromMonth', {
                count: Math.abs(oocCountChange),
                month: previousMonthLabel,
              })}
          </Text>

          <FadedText size="xs" renderAs="span" className={classNames.chartLabel}>
            {t('complianceWatchLastFourMonths')}
          </FadedText>
          <TrendChart points={data.oocUsers || []} trend={trend} />

          <dl className={classNames.metricsList}>
            <div>
              <FadedText size="xs" renderAs="dt">
                <span className={[classNames.legendDot, classNames.riskDot].join(' ')} />
                {t('complianceWatchAtRisk')}
              </FadedText>
              <Text variant="strong" size="xs" renderAs="dd">
                {data.atRiskCount || 0}
              </Text>
            </div>
            <div>
              <FadedText size="xs" renderAs="dt">
                <span className={[classNames.legendDot, classNames.graceDot].join(' ')} />
                {t('gracePeriod')}
              </FadedText>
              <Text variant="strong" size="xs" renderAs="dd">
                {data.gracePeriodCount || 0}
              </Text>
            </div>
            <div>
              <FadedText size="xs" renderAs="dt">
                <span className={[classNames.legendDot, classNames.taxDot].join(' ')} />
                {t('taxLiability')}
              </FadedText>
              <Text variant="strong" size="xs" renderAs="dd">
                {data.taxLiabilityCount || 0} {t('complianceWatchOf')} {currentOocCount}
              </Text>
            </div>
          </dl>
        </section>

        <section className={classNames.attentionPanel}>
          <div className={classNames.attentionHeader}>
            <div>
              <FadedText size="xs" renderAs="span" className={classNames.attentionTitle}>
                {t(
                  isProactive
                    ? 'complianceWatchAtRisk'
                    : hasActiveChanges
                    ? 'complianceWatchWhatChanged'
                    : 'complianceWatchCurrentlyNeedsAttention',
                )}
              </FadedText>
            </div>
          </div>

          {users.length ? (
            <ul className={classNames.driverList}>
              {isProactive && (
                <>
                  <FadedText
                    variant="strong"
                    size="xs"
                    renderAs="li"
                    className={classNames.driverGroupLabel}
                  >
                    {t('complianceWatchDriversApproachingOutOfCompliance', {
                      count: data.atRiskCount || 0,
                    })}
                  </FadedText>
                  {renderDriverRows(atRiskUsers, true)}
                </>
              )}

              {!isProactive && hasActiveChanges && (
                <>
                  {!!outOfComplianceUsers.length && (
                    <FadedText
                      variant="strong"
                      size="xs"
                      renderAs="li"
                      className={classNames.driverGroupLabel}
                    >
                      {t('complianceWatchNewlyOutOfCompliance', { count: newOocCount })}
                    </FadedText>
                  )}
                  {renderDriverRows(outOfComplianceUsers, true)}
                  {!!atRiskUsers.length && (
                    <FadedText
                      variant="strong"
                      size="xs"
                      renderAs="li"
                      className={classNames.driverGroupLabel}
                    >
                      {t('complianceWatchAtRisk')}
                    </FadedText>
                  )}
                  {renderDriverRows(atRiskUsers, true)}
                </>
              )}

              {!isProactive &&
                !hasActiveChanges &&
                renderDriverRows(users, user => user.type === 'at-risk')}
            </ul>
          ) : (
            <div className={classNames.emptyState}>
              <CheckCircleFilled />
              <Text variant="strong" size="sm">
                {t('complianceWatchAllClear')}
              </Text>
              <FadedText size="xs">{t('complianceWatchAllClearDescription')}</FadedText>
            </div>
          )}

          <div className={classNames.attentionFooter}>
            <FadedText size="xs" renderAs="span" className={classNames.footerHint}>
              {isProactive
                ? t('complianceWatchAllDriversInCompliance', {
                    count: data.currentMonthIcUsersCount || 0,
                  })
                : hiddenUsersCount > 0
                ? t('complianceWatchMoreDriversNeedAttention', { count: hiddenUsersCount })
                : t('complianceWatchStatusUpToDate')}
            </FadedText>
            <Link
              to={INTERNAL_LINKS.USER_PROGRAM_COMPLIANCE}
              onClick={() => trackComplianceWatchInteraction('footer_report_link_click')}
            >
              <LinkText variant="strong" size="xs" className={classNames.linkText}>
                {t(isProactive ? 'complianceWatchViewReport' : 'viewAll')} <ArrowRightOutlined />
              </LinkText>
            </Link>
          </div>
        </section>
      </div>
    </Box>
  );
};

DriverRow.propTypes = {
  showStatusBadge: PropTypes.bool,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    atRiskSituations: PropTypes.arrayOf(PropTypes.string),
    firstName: PropTypes.string,
    insuranceExpirationDate: PropTypes.string,
    lastName: PropTypes.string,
    oocComponents: PropTypes.arrayOf(PropTypes.string),
    type: PropTypes.string,
    vehicleYear: PropTypes.number,
  }).isRequired,
};

DriverRow.defaultProps = {
  showStatusBadge: true,
};

TrendChart.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number,
      month: PropTypes.number,
      year: PropTypes.number,
    }),
  ).isRequired,
  trend: PropTypes.oneOf(['down', 'flat', 'up']).isRequired,
};

ComplianceWatch.propTypes = {
  currentCompany: PropTypes.shape({
    _id: PropTypes.string,
    hasFavrProducts: PropTypes.bool,
  }).isRequired,
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
  t: PropTypes.func.isRequired,
};

ComplianceWatch.defaultProps = {
  groupList: [],
};

export default withNamespaces()(ComplianceWatch);
